import { Dialog, DialogTitle, Box, Typography, Chip, DialogContent, Divider, List, ListItem, Button, DialogActions } from "@mui/material";
import { alpha, Grid } from "@mui/system";
import CalendarIcon from '@mui/icons-material/CalendarToday';
import MoneyIcon from '@mui/icons-material/Money';
import ViewIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import React from "react";
import { ApartmentDto } from "../../../../types/propertyInventory/Apartment";

interface Props{
    paymentHistoryDialogOpen: boolean;
    setPaymentHistoryDialogOpen: (open: boolean) => void;
    apartmentById: ApartmentDto | null;
    setAddPaymentDialogOpen: (open: boolean) => void;
    setSelectedImage: (image: string) => void;
    setImageDialogOpen: (open: boolean) => void;
}

export const PaymentHistory = ({
    paymentHistoryDialogOpen,
    setPaymentHistoryDialogOpen,
    apartmentById,
    setAddPaymentDialogOpen,
    setSelectedImage,
    setImageDialogOpen
}: Props) => {
    return (
        <Dialog
            open={paymentHistoryDialogOpen}
            onClose={() => setPaymentHistoryDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ color: '#4DB6AC' }} />
                        <Typography variant="h6" fontWeight="bold">
                            Historial de Pagos
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            label={`${apartmentById?.currentTenant?.rentPayments?.length || 0} pago(s)`}
                            size="small"
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.1),
                                color: '#4DB6AC',
                                fontWeight: 'bold'
                            }}
                        />
                    </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {apartmentById?.currentTenant?.fullName} - {apartmentById?.apartmentNumber}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Resumen Financiero */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={4}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#4CAF50', 0.1),
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Total Pagado
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#4CAF50">
                                {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                {apartmentById?.currentTenant?.rentPayments
                                    ?.filter((p: any) => p.status === 2) // PaymentStatus.pagado
                                    ?.reduce((sum : any, p : any) => sum + p.paidAmount, 0)
                                    .toLocaleString() || 0}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={4}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#FF9800', 0.1),
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Pendiente
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#FF9800">
                                {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                {apartmentById?.currentTenant?.rentPayments
                                    ?.filter((p: any) => p.status === 1)
                                    ?.reduce((sum : any, p : any) => sum + (p.dueAmount || 0), 0)
                                    .toLocaleString() || 0}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={4}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#F44336', 0.1),
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Atrasados
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#F44336">
                                {apartmentById?.currentTenant?.rentPayments
                                    ?.filter((p: any) => p.status === 3) // PaymentStatus.atrasado
                                    ?.length || 0}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                {/* Lista de Pagos */}
                {apartmentById?.currentTenant?.rentPayments && apartmentById.currentTenant.rentPayments.length > 0 ? (
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {apartmentById.currentTenant.rentPayments
                            .sort((a: any, b: any) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())
                            .map((payment: any, index: number) => {
                                const getPaymentStatusInfo = (status: number) => {
                                    switch (status) {
                                        case 2: // pagado
                                            return { label: 'Pagado', color: '#4CAF50', bg: alpha('#4CAF50', 0.1) };
                                        case 1: // pendiente
                                            return { label: 'Pendiente', color: '#FF9800', bg: alpha('#FF9800', 0.1) };
                                        case 3: // atrasado
                                            return { label: 'Atrasado', color: '#F44336', bg: alpha('#F44336', 0.1) };
                                        case 4: // cancelado
                                            return { label: 'Cancelado', color: '#9E9E9E', bg: alpha('#9E9E9E', 0.1) };
                                        default:
                                            return { label: 'Desconocido', color: '#9E9E9E', bg: alpha('#9E9E9E', 0.1) };
                                    }
                                };

                                const getPaymentMethodText = (method: number) => {
                                    switch (method) {
                                        case 1: return '💵 Efectivo';
                                        case 2: return '🏦 Transferencia';
                                        case 3: return '💳 Tarjeta Crédito';
                                        case 4: return '💳 Tarjeta Débito';
                                        case 5: return '📱 SINPE';
                                        default: return 'No especificado';
                                    }
                                };

                                const statusInfo = getPaymentStatusInfo(payment.status);

                                return (
                                    <React.Fragment key={payment.id}>
                                        <ListItem
                                            sx={{
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                backgroundColor: index % 2 === 0 ? alpha('#4DB6AC', 0.02) : 'transparent',
                                                borderRadius: 2,
                                                mb: 1,
                                                p: 2
                                            }}
                                        >
                                            {/* Header del pago */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarIcon sx={{ fontSize: 18, color: '#4DB6AC' }} />
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {new Date(payment.paymentDate).toLocaleDateString('es-CR', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={statusInfo.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: statusInfo.bg,
                                                        color: statusInfo.color,
                                                        fontWeight: 'bold',
                                                        height: 24
                                                    }}
                                                />
                                            </Box>

                                            {/* Detalles del pago */}
                                            <Grid container spacing={2} sx={{ mb: 1 }}>

                                                <Grid size={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Monto Pagado
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="medium" color="#4CAF50">
                                                        {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                                        {payment.paidAmount.toLocaleString()}
                                                    </Typography>
                                                </Grid>

                                                {payment.dueAmount && payment.dueAmount > 0 && (
                                                    <Grid size={6}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Pendiente
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium" color="#FF9800">
                                                            {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                                            {payment.dueAmount.toLocaleString()}
                                                        </Typography>
                                                    </Grid>
                                                )}

                                                <Grid size={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Método de Pago
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {getPaymentMethodText(payment.paymentMethod)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {/* Notas y Acciones */}
                                            {payment.notes && (
                                                <Box
                                                    sx={{
                                                        backgroundColor: alpha('#4DB6AC', 0.05),
                                                        borderRadius: 1,
                                                        p: 1,
                                                        mb: 1
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Notas:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {payment.notes}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                {payment.receiptPhoto && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<ViewIcon />}
                                                        sx={{ color: '#4DB6AC' }}
                                                        onClick={() => {
                                                            setSelectedImage(payment.receiptPhoto || '');
                                                            setImageDialogOpen(true);
                                                        }}
                                                    >
                                                        Ver Comprobante
                                                    </Button>
                                                )}
                                                <Button
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    sx={{ color: '#FF9800' }}
                                                >
                                                    Editar
                                                </Button>
                                            </Box>
                                        </ListItem>
                                        {index < apartmentById.rentPayments.length - 1 && <Divider />}
                                    </React.Fragment>
                                );
                            })}
                    </List>
                ) : (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 6,
                            px: 3
                        }}
                    >
                        <MoneyIcon sx={{ fontSize: 60, color: '#E0E0E0', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No hay pagos registrados
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Aún no se han registrado pagos para este apartamento
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<MoneyIcon />}
                            onClick={() => {
                                setPaymentHistoryDialogOpen(false);
                                setAddPaymentDialogOpen(true);
                            }}
                            sx={{
                                backgroundColor: '#4DB6AC',
                                '&:hover': {
                                    backgroundColor: '#26A69A'
                                }
                            }}
                        >
                            Registrar Primer Pago
                        </Button>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Button
                    startIcon={<MoneyIcon />}
                    onClick={() => {
                        setPaymentHistoryDialogOpen(false);
                        setAddPaymentDialogOpen(true);
                    }}
                    sx={{ color: '#4DB6AC' }}
                >
                    Nuevo Pago
                </Button>
                <Button onClick={() => setPaymentHistoryDialogOpen(false)}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    )
}